/**
 * 참가자 종류별 UI 등록부 — 종류 레지스트리(participantKinds)는 UI 를 **조회만** 한다.
 *
 * 에디터 본문·보조 파일 편집기는 features 층의 컴포넌트다. 레지스트리(app)가 그것을 import 하면 층 방향이 뒤집힌다
 * (#389 에서 예외 5건으로 남겨 둔 것). 대신 features 쪽 선언(각 feature 폴더의 participantUi.register.ts)이 여기에 등록하고,
 * 앱 부트스트랩(`src/participantUiBootstrap.ts`)이 그 선언들을 import 한다.
 */
import type { ComponentType } from 'react';
import type { AuxEditorProps, AuxSlot, ParticipantPortOptions } from './participantKinds';
import type { ExchangeConfig, ParticipantConfig } from '@/types/cosim';

/**
 * 종류별 에디터 본문(MarsEditorBody·ModelicaEditorBody)의 공용 props 계약.
 * 셸이 프로젝트/모델 컨텍스트와 종류-무관 오케스트레이션 콜백을 내려준다.
 */
export interface EditorBodyProps {
  projectId: string;
  modelId: string;
  /** 셸이 관리하는 Co-Sim 우측 패널 활성 여부. */
  coSimActive: boolean;
  /** Co-Sim 활성 중 노드 편집이 시작되면 셸에 Co-Sim 종료를 요청. */
  onExitCoSim: () => void;
  /** 헤더에 표시할 프로젝트 이름(셸 결정). */
  projectName?: string;
  /** 헤더 뒤로가기(셸이 저장 후 이동). */
  onBack: () => void;
  /** 헤더 재생(▶) — 현재 편집 모델만 단독 실행(TEST RUN, 커플링 결선 제외)으로
   *  이동(셸이 저장 후). 전체(커플링) 실행 진입은 셸 사이드바 Simulation. */
  onRunSimulation: () => void;
  /** 헤더 전체저장(셸이 flush 후 전 모델 persist). */
  onSaveAll: () => void;
  saveAllVisible: boolean;
  savingAll: boolean;
  /** 탭을 떠나며 메모리에만 commit 한 미저장 편집이 있다(셸의 `unsavedFlushed`). 헤더 «저장 안 됨» 배지는 이것도 켠다 —
   *  배지가 이탈 창·beforeunload·이동 전 저장과 **같은 정본**을 읽어야 «배지 없음 = 저장됨» 이 참이다(#543). */
  hasFlushedUnsaved: boolean;
  /** 본문이 활성 모델 하나를 서버에 저장했다(헤더 [현재 탭 저장]) — 셸이 그 모델을 미저장 추적에서 지운다. 전체 저장은 셸이 직접 비운다. */
  onModelSaved: (modelId: string) => void;
}

/**
 * 커플링 설정 «참가자» 탭의 종류별 편집기(변수 결선·경계면·산출물 URI …) props — 탭 셸이 참가자마다 내려준다.
 * 탭은 종류를 가르지 않는다: 종류가 등록한 편집기가 그 참가자의 설정을 그린다.
 */
export interface CouplingParticipantEditorProps {
  /** 모델 순번(=참가자 인덱스) */
  index: number;
  participant: ParticipantConfig;
  /** 저작 모델이 있는 참가자인가 — 산출물은 시작 시 모델에서 만든다(URI 불요) */
  modelBacked: boolean;
  /** 결선의 정본(교환 목록) — 선택은 여기 해당 data 방향에 기록한다 */
  exchanges: ExchangeConfig[];
  setExchanges: (next: ExchangeConfig[]) => void;
  /** 모델에서 유도한 변수 선택지(종류 스펙 `authoring.loadPortOptions`, 검증과 같은 목록) — 모르면 null */
  portOptions: ParticipantPortOptions | null;
}

export interface ParticipantUi {
  /** 그래프 에디터 본문 — 지연 import(종류별 UI 트리셰이킹). 종류-무관 EditorShell 이 활성 모델 종류로 조회한다 */
  editorBody?(): Promise<{ default: ComponentType<EditorBodyProps> }>;
  /** 보조 파일 슬롯의 구조화 편집기 — 슬롯 선언(`participantKinds.AUX_SLOT_DECLS`)의 키로. 카드는 종류 스펙이 그 슬롯에
   *  `structuredEditor` 를 선언했을 때만 쓴다 */
  auxEditors?: Partial<Record<AuxSlot, () => Promise<{ default: ComponentType<AuxEditorProps> }>>>;
  /** 커플링 설정 참가자 탭의 종류별 편집기 — 없는 종류는 탭이 «편집기 미등록» 을 보인다 */
  couplingParticipantEditor?(): Promise<{ default: ComponentType<CouplingParticipantEditorProps> }>;
}

const UI = new Map<string, ParticipantUi>();

/** features 쪽 선언이 부른다 — 같은 종류를 다시 등록하면 병합(슬롯별 마지막 선언이 이긴다 — 보조 파일 편집기도 슬롯마다) */
export function registerParticipantUi(kind: string, ui: ParticipantUi): void {
  const prev = UI.get(kind);
  UI.set(kind, { ...prev, ...ui, auxEditors: { ...prev?.auxEditors, ...ui.auxEditors } });
}

export function participantUiOf(kind: string): ParticipantUi | undefined {
  return UI.get(kind);
}
